import type Luminary from '@lumen5/luminary';
import type { Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    greaterThanWithinEpsilon,
    lessThanOrEqualWithinEpsilon,
} from 'js/src/luminary/comparison-utils';
import {
    calculateInterestPointTimesForEntireSceneGroupOperation,
    reapplyInterestPointsOperation,
} from 'js/src/data/transcript/transcript-operations';
import type { InterestPointTime } from 'js/src/data/transcript/transcript-types';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

export const isVoicedrivenScene = (scene: Scene): boolean =>
    !!scene.sceneGroup && isVoiceDrivenSceneGroup(scene.sceneGroup);

const locateSceneGroupSnapshot = (scene: Readonly<Scene>) => {
    const sceneGroup = scene.sceneGroup;
    if (!sceneGroup) {
        throw new Error('Scene is not in a scene group');
    }
    return { sceneGroup, snapshot: _activeBatchSnapshots[sceneGroup.id] };
};

/**
 * @returns the scene's startTime in video time, as if there are no transitions
 */
export const getVoicedrivenSceneStartTime = (scene: Readonly<Scene>) => {
    const { sceneGroup, snapshot } = locateSceneGroupSnapshot(scene);
    const sceneGroupIsMidBatch = Boolean(snapshot);
    if (!sceneGroupIsMidBatch) {
        return scene.getLogicalStartTime();
    }
    // Only the mid-batch walk needs the index, and reading it scans the group.
    const sceneIndex = scene.localIndex;
    if (sceneIndex === -1) {
        throw new Error('Scene is not in its scene group');
    }
    return sceneGroup.scenes
        .slice(0, sceneIndex)
        .reduce(
            (startTime, currentScene) => startTime + getVoicedrivenSceneDuration(currentScene),
            sceneGroup.startTime,
        );
};

export const getVoicedrivenSceneEndTime = (scene: Readonly<Scene>) => {
    return getVoicedrivenSceneStartTime(scene) + getVoicedrivenSceneDuration(scene);
};

/**
 * Return the scenes in a voicedriven scene group whose transition-excluded (logical) span contains
 * the given video time.
 *
 * This is the voicedriven-time counterpart to `luminary.getActiveScenesAt`. That method queries the
 * real Luminary timeline, where a scene's incoming transition pulls its start earlier so it overlaps
 * the preceding scene — so a single point can report two active scenes even though the scenes tile
 * cleanly. Voicedriven overlay math works in transition-excluded video time (see
 * {@link getVoicedrivenSceneStartTime}), so callers reasoning in that space must resolve active
 * scenes the same way to avoid a phantom overlap around every transition.
 */
export const getVoicedrivenScenesActiveAt = (
    sceneGroup: SceneGroup,
    videoTime: number,
): Scene[] => {
    return sceneGroup.scenes.filter(
        (scene) =>
            lessThanOrEqualWithinEpsilon(getVoicedrivenSceneStartTime(scene), videoTime) &&
            greaterThanWithinEpsilon(getVoicedrivenSceneEndTime(scene), videoTime),
    );
};

export const getVoicedrivenSceneDuration = (scene: Readonly<Scene>) => {
    const { snapshot } = locateSceneGroupSnapshot(scene);
    const sceneGroupIsMidBatch = Boolean(snapshot);
    if (!sceneGroupIsMidBatch) {
        return scene.getLogicalDuration();
    }
    const entry = snapshot[scene.id];
    if (!entry || entry.durationType === DurationType.LOGICAL_DURATION) {
        // Either this scene was added inside the active batch (no snapshot entry) or it was
        // written via setVoicedrivenSceneDuration. In both cases scene.duration currently
        // holds the logical (without-transition) duration; the adjust pass will add the
        // transition back at the end of the batch.
        return scene.duration;
    }
    return scene.duration - entry.transition;
};

/**
 * Writes scene.duration as a logical (without-transition) duration and marks the scene as having
 * been written inside the active runVoicedrivenBatchRunOperation. The post-batch adjust pass uses
 * this marker to decide whether to add the transition back.
 *
 */
export const setVoicedrivenSceneDuration = (scene: Scene, duration: number) => {
    scene.duration = duration;
    const { snapshot } = locateSceneGroupSnapshot(scene);
    if (snapshot && snapshot[scene.id]) {
        snapshot[scene.id].durationType = DurationType.LOGICAL_DURATION;
    }
};

const _activeBatchSnapshots: Record<SceneGroupId, ScenesWithTransitionDuration> = {};
const _activeMorphSnapshots: Record<SceneGroupId, IncomingMorphEdges> = {};

export enum DurationType {
    LOGICAL_DURATION = 'LOGICAL_DURATION',
    DURATION_INCLUDING_TRANSITION = 'DURATION_INCLUDING_TRANSITION',
}

type TransitionDuration = number;
type SceneId = string;
type SceneGroupId = string;
type ScenesWithTransitionDuration = Record<
    SceneId,
    {
        transition: TransitionDuration;
        durationType: DurationType;
    }
>;

const calculateScenesWithTransitionDurations = (
    sceneGroup: SceneGroup,
): ScenesWithTransitionDuration => {
    return sceneGroup.scenes.reduce((acc, scene) => {
        acc[scene.id] = {
            transition: scene.duration - scene.getLogicalDuration(),
            durationType: DurationType.DURATION_INCLUDING_TRANSITION,
        };
        return acc;
    }, {} as ScenesWithTransitionDuration);
};

type IncomingMorphEdges = Record<SceneId, SceneId>;

export const sameGroupPrevScene = (scene: Scene): Scene | null => {
    const prev = scene.prevScene;
    return prev && prev.sceneGroup === scene.sceneGroup ? prev : null;
};

export const sameGroupNextScene = (scene: Scene): Scene | null => {
    const next = scene.nextScene;
    return next && next.sceneGroup === scene.sceneGroup ? next : null;
};

export const canCarryMorph = (scene: Scene): boolean =>
    scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).some((asset) => asset.linkToSceneGroup);

export const enableMorphFrom = (scene: Scene): void => {
    scene.morphEnabled = true;
    scene.getAssets({ deep: true }).forEach((asset) => {
        asset.morphInterpolation = VideoData.EASE_IN_OUT_INTERPOLATION_PRESET;
    });
};

const captureIncomingMorphEdges = (sceneGroup: SceneGroup): IncomingMorphEdges => {
    return sceneGroup.scenes.reduce((acc, scene) => {
        const prev = sameGroupPrevScene(scene);
        if (prev?.morphEnabled && !prev.isPrePackagedMorphWithNextScene) {
            acc[scene.id] = prev.id;
        }
        return acc;
    }, {} as IncomingMorphEdges);
};

const reconcileMorphEdgesForBatch = (sceneGroup: SceneGroup, snapshot: IncomingMorphEdges) => {
    const scenesById = new Map(sceneGroup.scenes.map((scene) => [scene.id, scene]));
    const toClear = new Set<string>();
    const toArm = new Set<string>();

    Object.entries(snapshot).forEach(([sceneId, from]) => {
        const scene = scenesById.get(sceneId);
        const prev = scene ? sameGroupPrevScene(scene) : null;
        if (prev?.id === from) {
            return;
        }
        toClear.add(from);
        if (scene && prev && canCarryMorph(prev) && canCarryMorph(scene)) {
            toArm.add(prev.id);
        }
    });

    toClear.forEach((sceneId) => {
        if (!toArm.has(sceneId)) {
            const scene = scenesById.get(sceneId);
            if (scene) {
                scene.morphEnabled = false;
            }
        }
    });
    toArm.forEach((sceneId) => {
        const scene = scenesById.get(sceneId);
        if (scene) {
            enableMorphFrom(scene);
        }
    });
};

const reconcileSceneDurationsForBatch = (
    sceneGroup: SceneGroup,
    snapshot: ScenesWithTransitionDuration,
) => {
    sceneGroup.scenes.forEach((scene) => {
        const entry = snapshot[scene.id];
        // Recover the visual span (without-transition duration) we need the
        // scene to preserve after reconciliation:
        // - LOGICAL_DURATION (or no entry — scene created inside batch):
        //   scene.duration currently IS that span; setVoicedrivenSceneDuration
        //   wrote it as the visible duration the caller asked for.
        // - DURATION_INCLUDING_TRANSITION: scene.duration still includes the
        //   snapshot-time transition, so subtract it to recover the span.
        const targetLogicalSpan =
            !entry || entry.durationType === DurationType.LOGICAL_DURATION
                ? scene.duration
                : scene.duration - entry.transition;

        const sceneCouldHaveTransitionWithinItsOwnGroup =
            scene.prevScene?.sceneGroup === scene.sceneGroup;
        if (!sceneCouldHaveTransitionWithinItsOwnGroup) {
            // No incoming transition applies to this scene. It might have had
            // a transition when the batch started (eg. it was the second scene in the scenegroup,
            // but it is now the first). We need to make sure that any prior transition
            // does not get included in the scene's new duration
            scene.duration = targetLogicalSpan;
            return;
        }

        scene.duration = scene.calculateDurationForLogicalSpan(targetLogicalSpan);
    });
};

/**
 * Non-operation variant of {@link runVoicedrivenBatchRunOperation}. Prefer the operation when
 * you're inside another luminary operation (so error reporting / dispatch propagate). Use this
 * plain function when you only have a Luminary instance and no `call` context — e.g. from a
 * scene-swapping bridge or other utility code.
 */
export const runVoicedrivenBatchRun = <T>(
    luminary: Luminary,
    sceneGroupId: string,
    cb: () => T,
): T => {
    if (_activeBatchSnapshots[sceneGroupId]) {
        // Already inside a voicedriven batch for this same scene group. Reads and writes
        // operate on the existing snapshot directly; that batch's adjust pass will handle
        // reconciliation.
        return luminary.batchRun(cb);
    }
    const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
    _activeBatchSnapshots[sceneGroupId] = sceneGroup
        ? calculateScenesWithTransitionDurations(sceneGroup)
        : {};
    _activeMorphSnapshots[sceneGroupId] = sceneGroup ? captureIncomingMorphEdges(sceneGroup) : {};
    try {
        return luminary.batchRun(() => {
            const retVal = cb();
            const sg = luminary.getSceneGroupById(sceneGroupId);
            const snapshot = _activeBatchSnapshots[sceneGroupId];
            if (sg && snapshot) {
                reconcileSceneDurationsForBatch(sg, snapshot);
                reconcileMorphEdgesForBatch(sg, _activeMorphSnapshots[sceneGroupId] ?? {});
            }
            return retVal;
        });
    } finally {
        delete _activeBatchSnapshots[sceneGroupId];
        delete _activeMorphSnapshots[sceneGroupId];
    }
};

/**
 * This function uses luminary.batchRun to make a bunch of changes to a voicedriven scene group.
 * During the batchRun, scenes can be treated as if they have no transitions - this simplifies
 * the mental model significantly. You should use:
 * - {@link getVoicedrivenSceneDuration}
 * - {@link setVoicedrivenSceneDuration}
 * - {@link getVoicedrivenSceneStartTime}
 * - {@link getVoicedrivenSceneEndTime}
 *
 * inside of the batchRun to read/write scene durations (as if there are no transitions).
 *
 * After the batchRun is complete, scene durations will be adjusted to add back the transitions.
 */
const runVoicedrivenBatchRunOperation = createLuminaryOperation(
    ({ luminary }, sceneGroupId: string, cb: () => unknown) =>
        runVoicedrivenBatchRun(luminary, sceneGroupId, cb),
);

/**
 * Runs a mutation on a voicedriven scene group inside {@link runVoicedrivenBatchRunOperation} while
 * preserving the transcript anchor of any custom-timed (user-overridden) assets.
 *
 * Interest points are captured before the mutation and reapplied after the batch's duration
 * reconcile, so a change that shifts scene/transition timing keeps each overridden asset on its
 * transcript word. This is the sibling of the scene-duration reconcile: that preserves each scene's
 * logical span across a transition change, this preserves each asset's transcript position.
 *
 * The captured interest points are returned so a caller can reapply them again from a later async
 * step via {@link runVoicedrivenTranscriptBatchWithPrecapturedInterestPointsOperation}.
 */
export const runVoicedrivenTranscriptBatchOperation = createLuminaryOperation(
    ({ call }, sceneGroupId: string, cb: () => void): InterestPointTime[] => {
        const interestPoints = call(
            calculateInterestPointTimesForEntireSceneGroupOperation,
            sceneGroupId,
        );
        call(
            runVoicedrivenTranscriptBatchWithPrecapturedInterestPointsOperation,
            sceneGroupId,
            cb,
            interestPoints,
        );
        return interestPoints;
    },
);

/**
 * Variant of {@link runVoicedrivenTranscriptBatchOperation} for when the interest points were
 * captured earlier — e.g. before an async step that shifts timing (an incoming transition whose
 * overlap only lands once its lottie loads), so the anchor reflects the pre-shift position.
 */
export const runVoicedrivenTranscriptBatchWithPrecapturedInterestPointsOperation =
    createLuminaryOperation(
        (
            { call },
            sceneGroupId: string,
            cb: () => void,
            precapturedInterestPoints: InterestPointTime[],
        ): void => {
            call(runVoicedrivenBatchRunOperation, sceneGroupId, cb);
            if (precapturedInterestPoints.length > 0) {
                call(reapplyInterestPointsOperation, precapturedInterestPoints, sceneGroupId);
            }
        },
    );

/**
 * Multi-scene-group companion to {@link runVoicedrivenTranscriptBatchOperation}. Nests a
 * {@link runVoicedrivenTranscriptBatchOperation} for every scene group — each owning its own
 * capture → batch → reapply — and runs `cb` at the innermost point, so a single action that shifts
 * transition timing across several voicedriven scene groups at once (e.g. flipping the video-wide
 * transitions toggle) keeps every overridden asset on its transcript word. Each group's anchors are
 * captured before `cb` (with every group's batch snapshot already open) and reapplied as its batch
 * unwinds. The nested batches double as the across-groups batch: each group's pre-`cb` snapshot
 * drives its post-`cb` reconcile, preserving every scene's logical span across a global change.
 */
export const runVoicedrivenTranscriptBatchAcrossSceneGroupsOperation = createLuminaryOperation(
    ({ call }, sceneGroupIds: readonly string[], cb: () => void): void => {
        const enterNext = (index: number): void => {
            if (index >= sceneGroupIds.length) {
                cb();
                return;
            }
            call(runVoicedrivenTranscriptBatchOperation, sceneGroupIds[index], () =>
                enterNext(index + 1),
            );
        };
        enterNext(0);
    },
);
